'use client';

import { useTranslation } from '@/context/LanguageContext';
import type { Language } from '@/context/LanguageContext';
import { useState } from 'react';
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';

interface LanguageSwitcherProps {
  variant?: 'compact' | 'pill' | 'dropdown' | 'mobile';
  className?: string;
}

export default function LanguageSwitcher({ variant = 'compact', className = '' }: LanguageSwitcherProps) {
  const { language, setLanguage } = useTranslation();
  const [isAnimating, setIsAnimating] = useState(false);
  
  const toggleLanguage = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    const newLang: Language = language === 'id' ? 'en' : 'id';
    setLanguage(newLang);
    setTimeout(() => setIsAnimating(false), 300);
  };

  if (variant === 'compact') {
    // Compact toggle with both flags, active one highlighted
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <button
          onClick={() => setLanguage('id')}
          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold transition-all duration-200 ${
            language === 'id' 
              ? 'bg-white text-unmulGreen shadow-sm' 
              : 'text-white hover:text-gray-200'
          }`}
          title="Bahasa Indonesia"
          aria-label="Switch to Indonesian"
        >
          <span>ID</span>
        </button>
        <span className="text-white">|</span>
        <button
          onClick={() => setLanguage('en')}
          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold transition-all duration-200 ${
            language === 'en' 
              ? 'bg-white text-unmulGreen shadow-sm' 
              : 'text-white hover:text-gray-200'
          }`}
          title="English"
          aria-label="Switch to English"
        >
          <span>EN</span>
        </button>
      </div>
    );
  }

  if (variant === 'pill') {
    // Modern pill toggle with sliding indicator
    return (
      <div className={`relative bg-gray-100 rounded-full p-0.5 flex ${className}`}>
        {/* Sliding background */}
        <div 
          className={`absolute top-0.5 bottom-0.5 w-1/2 bg-unmulGreen rounded-full transition-transform duration-300 ease-out ${
            language === 'en' ? 'translate-x-full' : 'translate-x-0'
          }`}
        />
        <button
          onClick={() => setLanguage('id')}
          className={`relative z-10 px-3 py-1.5 text-xs font-bold rounded-full transition-colors duration-200 ${
            language === 'id' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          ID
        </button>
        <button
          onClick={() => setLanguage('en')}
          className={`relative z-10 px-3 py-1.5 text-xs font-bold rounded-full transition-colors duration-200 ${
            language === 'en' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          EN
        </button>
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <div className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 ${className}`}>
        <span className="text-sm font-bold text-gray-700 flex items-center gap-2">
           <FontAwesomeIcon icon={faExchangeAlt} className="text-gray-400" />
           Bahasa / Language
        </span>
        <div className="flex bg-white rounded-md p-1 shadow-sm border border-gray-100">
             <button
              onClick={() => setLanguage('id')}
              className={`px-3 py-1.5 rounded text-xs font-bold transition-all ${
                language === 'id' 
                  ? 'bg-unmulGreen text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ID
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1.5 rounded text-xs font-bold transition-all ${
                language === 'en' 
                  ? 'bg-unmulGreen text-white shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              EN
            </button>
        </div>
      </div>
    );
  }

  // Dropdown variant with flags and labels
  return (
    <div className={`relative group ${className}`}>
      <button 
        onClick={toggleLanguage}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
      >
        <Image 
          src={language === 'id' ? 'https://flagcdn.com/w20/id.png' : 'https://flagcdn.com/w20/gb.png'} 
          alt={language === 'id' ? 'Indonesia' : 'English'} 
          width={20}
          height={15}
          className="w-5 h-auto rounded-sm shadow-sm"
        />
        <span className="text-sm font-medium text-gray-700">
          {language === 'id' ? 'ID' : 'EN'}
        </span>
        <FontAwesomeIcon icon={faExchangeAlt} className={`text-[10px] text-gray-400 transition-transform duration-300 ${isAnimating ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
}
